<template>
  <div>
    <v-container class="toolbar__container">
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete
            v-model="idEquipamento"
            :items="equipamentosComInventario"
            :item-text="rotuloEquipamento"
            item-value="id_equipamento"
            clearable
            :label="$t('statusDispositivos.filters.equipment')"
            :placeholder="$t('statusDispositivos.messages.allEquipments')"
            persistent-placeholder
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="situacao"
            :items="situacoes"
            item-text="text"
            item-value="value"
            clearable
            :label="$t('statusDispositivos.filters.situation')"
          />
        </v-col>
        <v-col cols="12" sm="2" md="2" class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn small fab color="primary darken-1" class="white--text" v-bind="attrs" v-on="on" :loading="loading" @click="popula">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('statusDispositivos.actions.search') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-row class="mt-1 mb-1">
      <v-col v-for="card in kpiCards" :key="card.key" cols="12" sm="6" md="3" class="d-flex">
        <v-card
          class="pa-3 flex-grow-1"
          style="border-radius: 10px; cursor: pointer"
          :outlined="situacao !== card.situacao"
          :color="situacao === card.situacao ? 'primary lighten-5' : undefined"
          @click="filtraPorKpi(card)"
        >
          <div class="text-caption grey--text text--darken-1">{{ card.label }}</div>
          <div class="text-h5 font-weight-bold" :style="{ color: card.color }">{{ card.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <merus-data-table
      :key="tableKey"
      cod-tabela="EQUIPAMENTOS__VALIDADORES_DIAGNOSTICO"
      :headers="headers"
      :items="itensFiltrados"
      :loading="loading"
      item-key="chave"
      @update:headers="headers = $event"
    >
      <template v-slot:toolbar>
        <CadastroToolbar
          :title="$t('statusDispositivos.modules.diagnostics')"
          :token="token"
          :items="itensFiltrados"
          csv-title="dispositivos_diagnostico"
          :csv-headers="headers"
          :pdf-title="$t('statusDispositivos.modules.diagnostics')"
          :pdf-headers="headers"
          pdf-filename="dispositivos_diagnostico"
          hide-search
        />
      </template>

      <template v-slot:item.situacao_extenso="{ item }">
        <v-chip small dark :color="corSituacao(item.situacao)">{{ item.situacao_extenso }}</v-chip>
      </template>
    </merus-data-table>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import MerusDataTable from '../../Tables/MerusDataTable.vue';
import CadastroToolbar from '../../Toolbars/CadastroToolbar.vue';
import dispositivosFormatters from './dispositivosFormatters';
import recorteEquipamentos from './recorteEquipamentos';

const SITUACOES = ['OK', 'FALTA_NO_APARELHO', 'SOBRA_NO_APARELHO'];

export default {
  name: 'Diagnostico',
  components: { MerusDataTable, CadastroToolbar },
  mixins: [dispositivosFormatters, recorteEquipamentos],
  props: {
    equipamentos: { type: Array, default: () => [] },
    token: { type: String, default: null },
  },
  data: () => ({
    idEquipamento: null,
    situacao: null,
    loading: false,
    itens: [],
    headers: [],
  }),
  computed: {
    // Diagnóstico compara a escala com o inventário: o do aparelho (ISAPI) ou o do nível de
    // acesso (HikCentral). Suite sem inventário nunca teria linha e só poluiria o filtro.
    equipamentosComInventario() {
      return (this.equipamentos || []).filter((e) => e.suporta_inventario == 1);
    },
    // Fonte do inventário pela suite ATUAL do leitor: vale também para quem ainda não tem cache.
    fontePorEquipamento() {
      const mapa = {};
      (this.equipamentos || []).forEach((e) => { mapa[e.id_equipamento] = e.fonte_inventario; });
      return mapa;
    },
    tableKey() {
      return `dispositivos-diagnostico-${this.$i18n.locale}`;
    },
    situacoes() {
      return SITUACOES.map((s) => ({ value: s, text: this.$t(`statusDispositivos.situation.${s}`) }));
    },
    itensFiltrados() {
      if (!this.situacao) return this.itens;
      return this.itens.filter((i) => i.situacao === this.situacao);
    },
    kpiCards() {
      const conta = (s) => this.itens.filter((i) => i.situacao === s).length;
      return [
        { key: 'total', situacao: null, label: this.$t('statusDispositivos.filters.all'), value: this.itens.length, color: '#1976d2' },
        { key: 'ok', situacao: 'OK', label: this.$t('statusDispositivos.kpis.ok'), value: conta('OK'), color: '#4caf50' },
        { key: 'missing', situacao: 'FALTA_NO_APARELHO', label: this.$t('statusDispositivos.kpis.missing'), value: conta('FALTA_NO_APARELHO'), color: '#ef6c00' },
        { key: 'extra', situacao: 'SOBRA_NO_APARELHO', label: this.$t('statusDispositivos.kpis.extra'), value: conta('SOBRA_NO_APARELHO'), color: '#c62828' },
      ];
    },
  },
  watch: {
    '$i18n.locale'() {
      this.initializeHeaders();
      this.reprocessaExtensos();
    },
  },
  created() {
    this.initializeHeaders();
    this.popula();
  },
  methods: {
    rotuloEquipamento(item) {
      return item.cod_veiculo ? `${item.nome_equipamento} (${item.cod_veiculo})` : item.nome_equipamento;
    },
    initializeHeaders() {
      const h = this.$t.bind(this);
      this.headers = [
        { text: h('statusDispositivos.headers.equipment'), value: 'nome_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.vehicle'), value: 'cod_veiculo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.registrationCode'), value: 'matricula', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.personName'), value: 'nome_completo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.situation'), value: 'situacao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.inSchedule'), value: 'na_escala_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.exception'), value: 'tipo_excecao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.onDevice'), value: 'no_aparelho_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.authorized'), value: 'autorizado_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.lastError'), value: 'ultimo_erro', sortable: true, filtrable: true },
      ];
    },
    corSituacao(situacao) {
      if (situacao === 'OK') return 'green';
      if (situacao === 'FALTA_NO_APARELHO') return 'orange darken-2';
      return 'red';
    },
    filtraPorKpi(card) {
      this.situacao = this.situacao === card.situacao ? null : card.situacao;
    },
    // No HikCentral a comparação é com o nível de acesso, não com o aparelho: o rótulo diz isso.
    situacaoExtenso(linha) {
      const nivel = this.fontePorEquipamento[linha.id_equipamento] === 'NIVEL_ACESSO';
      const chave = nivel && linha.situacao !== 'OK' ? linha.situacao.replace('_NO_APARELHO', '_NO_NIVEL') : linha.situacao;
      return this.traduzDominio('situation', chave);
    },
    noInventarioExtenso(linha) {
      const simNao = this.fmtSimNao(linha.no_aparelho);
      if (linha.no_aparelho != 1) return simNao;
      const nivel = this.fontePorEquipamento[linha.id_equipamento] === 'NIVEL_ACESSO';
      return `${simNao} (${this.$t(nivel ? 'statusDispositivos.inventory.accessLevel' : 'statusDispositivos.inventory.device')})`;
    },
    decora(linha) {
      return {
        ...linha,
        chave: `${linha.id_equipamento}-${linha.id_pessoa}`,
        situacao_extenso: this.situacaoExtenso(linha),
        na_escala_extenso: this.fmtSimNao(linha.na_escala),
        tipo_excecao_extenso: linha.tipo_excecao ? this.traduzDominio('access.type', linha.tipo_excecao) : '',
        no_aparelho_extenso: this.noInventarioExtenso(linha),
        autorizado_extenso: this.fmtSimNao(linha.autorizado_faceid),
      };
    },
    reprocessaExtensos() {
      this.itens = this.itens.map((i) => this.decora(i));
    },
    popula() {
      this.loading = true;
      const filtro = this.idEquipamento != null ? { id_equipamento: this.idEquipamento } : {};
      apiController.request(apiController.endpoints.isapiGetIsapidiagnostico, {
        data: filtro,
        onSuccess: (response) => {
          this.itens = this.recortaDaLista(response.data || []).map((linha) => this.decora(linha));
        },
        onError: () => {
          this.itens = [];
        },
      })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
